import { useState, useEffect, useRef } from 'react';

// Ljudnivå + tystnadsvakt för inspelningsknappen.
//
// - soundLevel: RMS 0–1 (driver animeringen kring inspelningsknappen).
// - noAudioDetected: true om ingen ljudnivå över SILENCE_THRESHOLD upptäckts
//   på WARN_AFTER_MS strax efter start (eller efter att ljudet tystnat). Nollas
//   automatiskt så fort ljud kommer tillbaka. Icke-blockerande: bara en signal
//   till UI:t om att inget ljud fångas (talar användaren? rätt mikrofon vald?).
//
// selectedMicId gör att mätningen sker på SAMMA mikrofon som spelas in (samma
// deviceId-constraint som TranscriptionArea) — annars mäts default-mic och
// cirkeln/varningen speglar fel enhet.

// Normaliserad nivå (rms*2, cappad 1) räknas som "ljud" över detta. Tydligt tal
// ligger klart över; rumsbrus/tyst mic ligger under.
const SILENCE_THRESHOLD = 0.04;
// Hur länge det ska vara tyst innan varningen visas (grace efter start).
// Tillräckligt långt för att vanliga pauser i ett patientmöte (undersökning,
// anteckningar) inte ska få varningen att blinka.
const WARN_AFTER_MS = 8000;

export const useAudioLevel = ({ isRecording, isPaused, selectedMicId }) => {
  const [soundLevel, setSoundLevel] = useState(0);
  const [noAudioDetected, setNoAudioDetected] = useState(false);
  // Speglar noAudioDetected inuti onaudioprocess (undviker stale closure +
  // onödiga setState-anrop varje ljudbuffert).
  const noAudioRef = useRef(false);

  useEffect(() => {
    let audioContext;
    let analyser;
    let microphone;
    let javascriptNode;
    let stream;
    let cancelled = false;

    const initAudio = () => {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      // Grace-period: räkna starten som "senast ljud" så vi inte varnar direkt.
      // Sätts om när mikrofonen faktiskt öppnats (getUserMedia kan dröja).
      let lastSoundTs = Date.now();

      navigator.mediaDevices
        .getUserMedia({
          audio: selectedMicId ? { deviceId: { exact: selectedMicId } } : true,
        })
        .then((s) => {
          // Effekten hann städas innan strömmen kom → stäng direkt, starta inget.
          if (cancelled) {
            s.getTracks().forEach((t) => t.stop());
            return;
          }
          stream = s;
          lastSoundTs = Date.now();
          microphone = audioContext.createMediaStreamSource(stream);
          microphone.connect(analyser);
          javascriptNode = audioContext.createScriptProcessor(2048, 1, 1);
          analyser.connect(javascriptNode);
          javascriptNode.connect(audioContext.destination);

          javascriptNode.onaudioprocess = () => {
            analyser.getByteTimeDomainData(dataArray);
            let sum = 0;
            for (let i = 0; i < bufferLength; i++) {
              const sample = (dataArray[i] - 128) / 128;
              sum += sample * sample;
            }
            const rms = Math.sqrt(sum / bufferLength);
            const normalized = Math.min(rms * 2, 1);
            setSoundLevel(normalized);

            // Tystnadsvakt — onaudioprocess fyrar kontinuerligt även vid tystnad.
            const now = Date.now();
            if (normalized > SILENCE_THRESHOLD) {
              lastSoundTs = now;
              if (noAudioRef.current) {
                noAudioRef.current = false;
                setNoAudioDetected(false);
              }
            } else if (!noAudioRef.current && now - lastSoundTs > WARN_AFTER_MS) {
              noAudioRef.current = true;
              setNoAudioDetected(true);
            }
          };
        })
        .catch((err) => {
          console.error('Kunde inte få tillgång till mikrofon för ljudnivå:', err);
          // Mikrofonen gick inte att öppna (t.ex. vald mic urkopplad) — just det
          // fallet varningen finns för, så visa den direkt.
          if (!cancelled) {
            noAudioRef.current = true;
            setNoAudioDetected(true);
          }
        });
    };

    if (isRecording && !isPaused) {
      noAudioRef.current = false;
      setNoAudioDetected(false);
      initAudio();
    } else {
      setSoundLevel(0);
      noAudioRef.current = false;
      setNoAudioDetected(false);
    }

    return () => {
      cancelled = true;
      if (javascriptNode) {
        javascriptNode.onaudioprocess = null;
        javascriptNode.disconnect();
      }
      if (microphone) microphone.disconnect();
      if (analyser) analyser.disconnect();
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (audioContext && audioContext.state !== 'closed') audioContext.close();
    };
  }, [isRecording, isPaused, selectedMicId]);

  return { soundLevel, noAudioDetected };
};
